import React, { useEffect, useState } from "react";
import apiDefinitions from "../../../core/api/apiDefinitions";
import { showError } from "../../../core/alert/sweetAlertConfig";

interface KeyAllocationCounts {
  returnedKeyCount: number;
  issuedKeyCount: number;
  unreturnedKeyCount: number;
  date?: string;
}

const KeyAllocationModule: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<KeyAllocationCounts | null>(null);

  const fetchKeyCounts = async () => {
    setLoading(true);
    try {
      const response = await apiDefinitions.getAllocatedKeyCounts();
      if (response?.data?.status === "S1000") {
        setData(response.data.data);
      } else {
        showError(
          "Failed to fetch key counts: " +
            (response?.data?.statusDescription || "Unknown error")
        );
      }
    } catch (error: any) {
      showError(
        "Failed to fetch key counts: " +
          (error.response?.data?.statusDescription || "Network error")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeyCounts();
  }, []);

  const total =
    (data?.issuedKeyCount || 0) +
    (data?.returnedKeyCount || 0) +
    (data?.unreturnedKeyCount || 0);

  return (
    <div className="rm-glass-card rm-glass-card-interactive flex-fill w-100 p-3 p-md-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-3 mb-3 border-bottom border-light">
        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: "42px",
              height: "42px",
              background: "rgba(245, 158, 11, 0.12)",
              color: "var(--rm-warning)",
              fontSize: "1.25rem",
            }}
          >
            <i className="ti ti-key" />
          </div>
          <div>
            <h5 className="m-0 fw-bold" style={{ color: "var(--rm-text-primary)" }}>
              Key Allocation Summary
            </h5>
            <small style={{ color: "var(--rm-text-muted)" }}>
              {loading
                ? "Refreshing..."
                : `Total Tracked: ${total} ${
                    data?.date ? `• as of ${new Date(data.date).toLocaleDateString()}` : ""
                  }`}
            </small>
          </div>
        </div>

        <button
          onClick={fetchKeyCounts}
          disabled={loading}
          className="btn btn-sm btn-light rounded-pill px-3 shadow-sm border-0 d-inline-flex align-items-center gap-1"
          style={{ fontSize: "0.8rem", color: "var(--rm-text-secondary)" }}
          title="Refresh Data"
        >
          <i className={`ti ti-refresh ${loading ? "rotate" : ""}`} />
          <span className="d-none d-sm-inline">Refresh</span>
        </button>
      </div>

      {/* Body */}
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-warning mb-2" role="status" />
          <p className="small mb-0 text-muted">Updating key allocation records...</p>
        </div>
      ) : (
        <div className="row g-2 g-md-3">
          {/* Issued Keys */}
          <div className="col-12 col-sm-4">
            <div
              className="rm-sub-glass-item text-center p-3"
              style={{ borderLeft: "3px solid var(--rm-primary)" }}
            >
              <div
                className="rm-sub-glass-num fw-bold mb-1"
                style={{ color: "var(--rm-primary)" }}
              >
                {data?.issuedKeyCount ?? 0}
              </div>
              <small className="d-block text-truncate" style={{ color: "var(--rm-text-secondary)", fontSize: "0.8rem", fontWeight: 600 }}>
                Issued Keys
              </small>
            </div>
          </div>

          {/* Returned Keys */}
          <div className="col-12 col-sm-4">
            <div
              className="rm-sub-glass-item text-center p-3"
              style={{ borderLeft: "3px solid var(--rm-success)" }}
            >
              <div
                className="rm-sub-glass-num fw-bold mb-1"
                style={{ color: "var(--rm-success)" }}
              >
                {data?.returnedKeyCount ?? 0}
              </div>
              <small className="d-block text-truncate" style={{ color: "var(--rm-text-secondary)", fontSize: "0.8rem", fontWeight: 600 }}>
                Returned Keys
              </small>
            </div>
          </div>

          {/* Unreturned Keys */}
          <div className="col-12 col-sm-4">
            <div
              className="rm-sub-glass-item text-center p-3"
              style={{ borderLeft: "3px solid var(--rm-danger)", background: "rgba(239, 68, 68, 0.04)" }}
            >
              <div
                className="rm-sub-glass-num fw-bold mb-1"
                style={{ color: "var(--rm-danger)" }}
              >
                {data?.unreturnedKeyCount ?? 0}
              </div>
              <small className="d-block text-truncate" style={{ color: "var(--rm-danger)", fontSize: "0.8rem", fontWeight: 700 }}>
                Unreturned Keys
              </small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeyAllocationModule;
